import { CellHeader } from 'components/common';
import { uniqueId } from 'lodash';
import { useMemo } from 'react';

import {
  Box,
  CSS,
  Flex,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Typography,
} from '@project44/manifest-ds';
import { Skeleton } from '@project44/shared-components';
import { useTranslation } from '@project44/translations-utils';

import { ListTablePlaceholder } from './ListTablePlaceholder';
import { PaginationConfig } from './types';
import { getPaginationCount, getSkeletonCount } from './utils';

const styleCell = {
  backgroundColor: 'white',
  maxWidth: 310,
  position: 'sticky',
  '&:first-of-type': {
    boxShadow: 'inset -1px 0px 0 0 #E4E6E7', // $border-primary
    left: 0,
    zIndex: 2,
  },
};

interface IListTable<TData> {
  data: Array<TData>;
  doSearch?: () => void;
  handleClear?: () => void;
  headers: Array<{
    id: string;
    label: string;
    enabled?: boolean;
    // TODO fix generic typing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element?: (props: any) => JSX.Element;
    options?: Array<{
      columnLabel?: string;
      displayLabel?: string;
      label: string;
      value: string;
    }>;
  }>;
  isError: boolean;
  isLoading: boolean;
  // TODO fix generic typing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowClick: (row: any) => void;
  title: string;
  css?: CSS;
  pagination?: PaginationConfig;
  showClearButton?: boolean;
  skeletonRowCount?: number;
}

function ListTable<T>({
  css,
  data,
  doSearch,
  handleClear,
  headers,
  isError,
  isLoading,
  pagination,
  rowClick,
  showClearButton,
  title,
  skeletonRowCount,
}: IListTable<T>) {
  const { t } = useTranslation();

  const tableHeaders = useMemo(() => {
    const enabled = headers.filter(({ enabled }) => enabled);
    return enabled.map(({ id, label, options }) => (
      <CellHeader
        key={id}
        disabled={!data.length}
        doSearch={doSearch}
        isLoading={isLoading}
        options={options}
        title={label}
      />
    ));
  }, [data, doSearch, headers, isLoading]);

  const tableRows = useMemo(() => {
    const enabled = headers.filter(({ enabled }) => enabled);
    if (isLoading) {
      const rows = getSkeletonCount(skeletonRowCount || 10);
      const columns = getSkeletonCount(enabled.length);
      return rows.map((rowKey) => (
        <TableRow
          key={rowKey}
          children={columns.map((columnId) => (
            <TableCell key={columnId} css={styleCell}>
              <Skeleton css={{ padding: '$medium' }} />
            </TableCell>
          ))}
        />
      ));
    }
    return data.map((row) => (
      <TableRow
        key={uniqueId()}
        onClick={() => rowClick(row)}
        css={{ cursor: 'pointer' }}
        children={enabled.map(({ id, element }) => (
          <TableCell key={id} css={styleCell}>
            {typeof element === 'function' ? element(row) : element}
          </TableCell>
        ))}
      />
    ));
  }, [data, headers, isLoading, rowClick, skeletonRowCount]);

  return (
    <Flex
      orientation="vertical"
      css={{
        backgroundColor: 'white',
        borderRadius: '$small',
        boxShadow: '$small',
        flex: 1,
        overflow: 'hidden',
        ...css,
      }}
    >
      <Box
        css={{
          overflowY: 'auto',
          flex: 1,
          ...(!tableRows.length && { flex: 'auto 0' }),
        }}
      >
        <Table isDense>
          <TableHeader>
            <TableRow children={tableHeaders} />
          </TableHeader>
          <TableBody children={tableRows} />
        </Table>
      </Box>

      {!tableRows.length && (
        <ListTablePlaceholder
          isError={isError || false}
          onClear={handleClear}
          showClearButton={showClearButton || false}
          title={title || t('core.items')}
        />
      )}

      {pagination && !!tableRows.length && (
        <Flex
          align="center"
          justify="between"
          css={{ padding: '$medium', borderTop: '1px solid $border-primary' }}
        >
          <Typography variant="subtextBold" css={{ color: '$text-tertiary' }}>
            {getPaginationCount(pagination)}
          </Typography>
          <Pagination
            onChange={pagination.onPageChange}
            page={pagination.pageNumber}
            rowsPerPage={pagination.pageSize}
            showPageNumbers={false}
            totalRowCount={pagination.total}
            nextLabel={t('global.next')}
            previousLabel={t('global.previous')}
          />
        </Flex>
      )}
    </Flex>
  );
}

export default ListTable;
